import { ALStream, FundingSource, Gender, LeadDetail, OlAlType, SubjectResult } from '../../../types/lead';
import { CreateLeadPayload } from '../../../api/leads';

/** '' represents "not chosen yet" for every select/number field — MUI Select/TextField
 * need a defined value to stay controlled, and CreateLeadPayload fields are all optional. */
export interface WizardValues {
  firstName: string;
  lastName: string;
  gender: Gender | '';
  dateOfBirth: string;
  nicOrPassport: string;
  nationalityCountryId: number | '';
  email: string;
  phone: string;
  telephone: string;
  address: string;
  city: string;
  postalCode: string;
  branchId: number | '';
  assignedToId: number | '';
  leadSourceId: number | '';

  schoolName: string;
  olType: OlAlType | '';
  olYear: number | '';
  alType: OlAlType | '';
  alYear: number | '';
  alStream: ALStream | '';
  overallGpa: number | '';

  preferredQualificationLevelId: number | '';
  preferredStudyAreaId: number | '';
  countryId: number | '';
  preferredUniversityId: number | '';
  // Lead Registration correction: free text, replacing the old
  // preferredIntakeId dropdown — that column still exists and keeps
  // whatever a historical record already has, but this wizard no longer
  // collects or overwrites it. Budget/budgetCurrencyId removed from this
  // step entirely (also still real, untouched columns).
  preferredIntakeLabel: string;

  fatherName: string;
  fatherPhone: string;
  fatherOccupation: string;
  fatherOrganization: string;
  fatherOfficePhone: string;
  fatherEmail: string;
  motherName: string;
  motherPhone: string;
  motherOccupation: string;
  motherOrganization: string;
  motherOfficePhone: string;
  motherEmail: string;
  parentsMet: boolean | null;
  guardianName: string;
  guardianRelationship: string;
  guardianPhone: string;
  guardianEmail: string;

  expectedInvestment: number | '';
  incomeRange: string;
  monthlyIncome: number | '';
  scholarshipRequired: boolean;
  fundingSource: FundingSource | '';
  sponsorDetails: string;

  campaignId: number | '';
  referralTypeId: number | '';
  referrerName: string;
  referrerContact: string;
  // Ad tracking: the specific advertisement/campaign identifier, distinct
  // from leadSourceId (the channel) — manual entry.
  adIdName: string;
}

export interface SubjectResultDraft {
  localId: string;
  subject: string;
  grade: string;
}

export const emptyWizardValues: WizardValues = {
  firstName: '',
  lastName: '',
  gender: '',
  dateOfBirth: '',
  nicOrPassport: '',
  nationalityCountryId: '',
  email: '',
  phone: '',
  telephone: '',
  address: '',
  city: '',
  postalCode: '',
  branchId: '',
  assignedToId: '',
  leadSourceId: '',

  schoolName: '',
  olType: '',
  olYear: '',
  alType: '',
  alYear: '',
  alStream: '',
  overallGpa: '',

  preferredQualificationLevelId: '',
  preferredStudyAreaId: '',
  countryId: '',
  preferredUniversityId: '',
  preferredIntakeLabel: '',

  fatherName: '',
  fatherPhone: '',
  fatherOccupation: '',
  fatherOrganization: '',
  fatherOfficePhone: '',
  fatherEmail: '',
  motherName: '',
  motherPhone: '',
  motherOccupation: '',
  motherOrganization: '',
  motherOfficePhone: '',
  motherEmail: '',
  parentsMet: null,
  guardianName: '',
  guardianRelationship: '',
  guardianPhone: '',
  guardianEmail: '',

  expectedInvestment: '',
  incomeRange: '',
  monthlyIncome: '',
  scholarshipRequired: false,
  fundingSource: '',
  sponsorDetails: '',

  campaignId: '',
  referralTypeId: '',
  referrerName: '',
  referrerContact: '',
  adIdName: '',
};

export const emptySubjectResultDraft = (): SubjectResultDraft => ({
  localId: crypto.randomUUID(),
  subject: '',
  grade: '',
});

const draftsToResults = (drafts: SubjectResultDraft[]): SubjectResult[] | undefined => {
  const results = drafts.filter((d) => d.subject.trim()).map((d) => ({ subject: d.subject.trim(), grade: d.grade.trim() || undefined }));
  return results.length > 0 ? results : undefined;
};

const num = (v: number | '') => (v === '' ? undefined : v);
const str = (v: string) => (v.trim() === '' ? undefined : v.trim());

/** Builds the API payload from wizard state, converting '' sentinels to undefined
 * (CreateLeadPayload fields are all optional — '' would otherwise fail backend
 * validation, e.g. z.coerce.date() rejects '' as an invalid date). */
export const buildLeadPayload = (
  values: WizardValues,
  olResults: SubjectResultDraft[],
  alResults: SubjectResultDraft[],
): CreateLeadPayload => ({
  firstName: values.firstName.trim(),
  lastName: values.lastName.trim(),
  gender: values.gender || undefined,
  dateOfBirth: str(values.dateOfBirth),
  nicOrPassport: str(values.nicOrPassport),
  nationalityCountryId: num(values.nationalityCountryId),
  email: str(values.email),
  phone: values.phone.trim(),
  telephone: str(values.telephone),
  address: str(values.address),
  city: str(values.city),
  postalCode: str(values.postalCode),
  branchId: num(values.branchId),
  leadSourceId: values.leadSourceId === '' ? undefined : values.leadSourceId,

  schoolName: str(values.schoolName),
  olType: values.olType || undefined,
  olYear: num(values.olYear),
  olResults: draftsToResults(olResults),
  alType: values.alType || undefined,
  alYear: num(values.alYear),
  alStream: values.alStream || undefined,
  alResults: draftsToResults(alResults),
  overallGpa: num(values.overallGpa),

  preferredQualificationLevelId: num(values.preferredQualificationLevelId),
  preferredStudyAreaId: num(values.preferredStudyAreaId),
  countryId: num(values.countryId),
  preferredUniversityId: num(values.preferredUniversityId),
  preferredIntakeLabel: str(values.preferredIntakeLabel),

  fatherName: str(values.fatherName),
  fatherPhone: str(values.fatherPhone),
  fatherOccupation: str(values.fatherOccupation),
  fatherOrganization: str(values.fatherOrganization),
  fatherOfficePhone: str(values.fatherOfficePhone),
  fatherEmail: str(values.fatherEmail),
  motherName: str(values.motherName),
  motherPhone: str(values.motherPhone),
  motherOccupation: str(values.motherOccupation),
  motherOrganization: str(values.motherOrganization),
  motherOfficePhone: str(values.motherOfficePhone),
  motherEmail: str(values.motherEmail),
  parentsMet: values.parentsMet ?? undefined,
  guardianName: str(values.guardianName),
  guardianRelationship: str(values.guardianRelationship),
  guardianPhone: str(values.guardianPhone),
  guardianEmail: str(values.guardianEmail),

  expectedInvestment: num(values.expectedInvestment),
  incomeRange: str(values.incomeRange),
  monthlyIncome: num(values.monthlyIncome),
  scholarshipRequired: values.scholarshipRequired,
  fundingSource: values.fundingSource || undefined,
  sponsorDetails: str(values.sponsorDetails),

  campaignId: num(values.campaignId),
  referralTypeId: num(values.referralTypeId),
  referrerName: str(values.referrerName),
  referrerContact: str(values.referrerContact),
  adIdName: str(values.adIdName),
});

/** Populates wizard state when resuming an existing draft lead. */
export const leadToWizardValues = (lead: LeadDetail): WizardValues => ({
  firstName: lead.firstName,
  lastName: lead.lastName,
  gender: lead.gender ?? '',
  dateOfBirth: lead.dateOfBirth ? lead.dateOfBirth.slice(0, 10) : '',
  nicOrPassport: lead.nicOrPassport ?? '',
  nationalityCountryId: lead.nationalityCountry?.id ?? '',
  email: lead.email ?? '',
  phone: lead.phone,
  telephone: lead.telephone ?? '',
  address: lead.address ?? '',
  city: lead.city ?? '',
  postalCode: lead.postalCode ?? '',
  branchId: lead.branch?.id ?? '',
  assignedToId: lead.assignedTo?.id ?? '',
  leadSourceId: lead.leadSource.id,

  schoolName: lead.schoolName ?? '',
  olType: lead.olType ?? '',
  olYear: lead.olYear ?? '',
  alType: lead.alType ?? '',
  alYear: lead.alYear ?? '',
  alStream: lead.alStream ?? '',
  overallGpa: lead.overallGpa ? Number(lead.overallGpa) : '',

  preferredQualificationLevelId: lead.preferredQualificationLevel?.id ?? '',
  preferredStudyAreaId: lead.preferredStudyArea?.id ?? '',
  countryId: lead.country?.id ?? '',
  preferredUniversityId: lead.preferredUniversity?.id ?? '',
  // Falls back to the historical Intake row's name so resuming an old
  // draft doesn't show a blank field where a value already existed —
  // never invented if genuinely never set.
  preferredIntakeLabel: lead.preferredIntakeLabel ?? lead.preferredIntake?.name ?? '',

  fatherName: lead.fatherName ?? '',
  fatherPhone: lead.fatherPhone ?? '',
  fatherOccupation: lead.fatherOccupation ?? '',
  fatherOrganization: lead.fatherOrganization ?? '',
  fatherOfficePhone: lead.fatherOfficePhone ?? '',
  fatherEmail: lead.fatherEmail ?? '',
  motherName: lead.motherName ?? '',
  motherPhone: lead.motherPhone ?? '',
  motherOccupation: lead.motherOccupation ?? '',
  motherOrganization: lead.motherOrganization ?? '',
  motherOfficePhone: lead.motherOfficePhone ?? '',
  motherEmail: lead.motherEmail ?? '',
  parentsMet: lead.parentsMet,
  guardianName: lead.guardianName ?? '',
  guardianRelationship: lead.guardianRelationship ?? '',
  guardianPhone: lead.guardianPhone ?? '',
  guardianEmail: lead.guardianEmail ?? '',

  expectedInvestment: lead.expectedInvestment ? Number(lead.expectedInvestment) : '',
  incomeRange: lead.incomeRange ?? '',
  monthlyIncome: lead.monthlyIncome ? Number(lead.monthlyIncome) : '',
  scholarshipRequired: lead.scholarshipRequired,
  fundingSource: lead.fundingSource ?? '',
  sponsorDetails: lead.sponsorDetails ?? '',

  campaignId: lead.campaign?.id ?? '',
  referralTypeId: lead.referralType?.id ?? '',
  referrerName: lead.referrerName ?? '',
  referrerContact: lead.referrerContact ?? '',
  adIdName: lead.adIdName ?? '',
});

export const resultsToDrafts = (results: SubjectResult[] | null): SubjectResultDraft[] =>
  (results ?? []).map((r) => ({ localId: crypto.randomUUID(), subject: r.subject, grade: r.grade ?? '' }));
